<template>
  <div>
    <v-progress-linear
      v-if="downloadStarted"
      indeterminate
      absolute
      top
      color="primary"
    ></v-progress-linear>
    <div class="flex flex-row items-center space-x-10">
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('search')"
        hide-details
        class="mb-4"
      />
    </div>

    <v-data-table
      :headers="getHeaders"
      :items="components"
      :search="search"
      class="elevation-1 certifai-table"
      :loading="loading"
      :items-per-page="5"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-arrow-collapse-left',
        lastIcon: 'mdi-arrow-collapse-right',
        prevIcon: 'mdi-arrow-left',
        nextIcon: 'mdi-arrow-right',
        disableItemsPerPage: false,
        itemsPerPageText: getItemsPerPageText,
        showCurrentPage: true,
      }"
      must-sort
      :sort-desc="true"
    >
      <template v-slot:[`item.status`]="{ item }">
        <v-chip :color="statusChipColor(item.status)" outlined dark>
          <span class="text-capitalize">{{ $t(item.status) }}</span>
        </v-chip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top v-if="hasUserMetadata(item)">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="g-action-btn g-action-view"
              v-on="on"
              v-bind="attrs"
              @click="enablePreviewAssetDialog(item.name)"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('preview') }}</span>
        </v-tooltip>

        <v-tooltip top v-if="item.status === 'Draft'">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="g-action-btn g-action-delete"
              v-on="on"
              v-bind="attrs"
              @click="enableDeleteDialog(item)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('delete.message') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="g-action-btn g-action-download"
              v-on="on"
              v-bind="attrs"
              @click="downloadDataset(item)"
            >
              <v-icon small>mdi-download</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('download') }}</span>
        </v-tooltip>
        <v-tooltip top v-if="item.status === 'Draft'">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="g-action-btn g-action-warning"
              v-on="on"
              v-bind="attrs"
              @click="enableFinalizeDialog(item)"
            >
              <v-icon small>mdi-lock</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('finalize.message') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog v-if="studyInfo" v-model="previewAssetClicked" max-width="700">
      <v-card>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn icon @click="previewAssetClicked = false" color="red">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-card-actions>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.imagingStudy.resourceType') }}: </strong>
                  {{ studyInfo.resourceType }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.imagingStudy.BodyPartExamined') }}: </strong>
                  {{
                    studyInfo.series[0].extension.find(e => e.url.includes('BodyPartExamined'))
                      .valueString
                  }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.imagingStudy.CodeMeaning') }}: </strong>
                  {{
                    studyInfo.series[0].extension.find(e => e.url.includes('CodeMeaning'))
                      .valueString
                  }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.imagingStudy.CodingSchemeDesignator') }}: </strong>
                  {{
                    studyInfo.series[0].extension.find(e =>
                      e.url.includes('CodingSchemeDesignator'),
                    ).valueString
                  }}
                </div>
              </v-col>
              <v-col cols="12">
                <div>
                  <strong>{{ $t('asset.imagingStudy.SOPClassUID') }}: </strong>
                  {{
                    studyInfo.series[0].extension.find(e => e.url.includes('SOPClassUID'))
                      .valueString
                  }}
                </div>
              </v-col>
              <v-col cols="12">
                <div>
                  <strong>{{ $t('asset.imagingStudy.subject') }}: </strong>
                  {{ studyInfo.subject.reference }}
                </div>
              </v-col>
              <v-col cols="12">
                <div>
                  <strong>{{ $t('asset.imagingStudy.originURL') }}: </strong>
                  <a
                    :href="studyInfo.extension.find(e => e.url.includes('originURL')).valueString"
                    target="_blank"
                  >
                    {{ studyInfo.extension.find(e => e.url.includes('originURL')).valueString }}
                  </a>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteClicked" max-width="500">
      <v-card>
        <v-card-title class="headline"
          >{{ $t('delete.message') }} {{ $tc('component.title', 1) }}</v-card-title
        >
        <v-card-text>
          {{ $t('delete.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="deleteClicked = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="red darken-1" text :loading="this.loading" @click="deleteAsset">
            {{ $t('continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="finalizeClicked" max-width="500">
      <v-card>
        <v-card-title class="headline"
          >{{ $t('finalize.message') }} {{ $tc('components.title', 1) }}</v-card-title
        >
        <v-card-text>
          {{ $t('finalize.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="finalizeClicked = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="red darken-1" :loading="this.loading" text @click="finalizeAsset">
            {{ $t('continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import { Fhir } from '@/modules/asset/api';

export default {
  name: 'ComponentList',
  mixins: [util],
  computed: {
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('component.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('component.headers.name'),
          value: 'name',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.id'),
          value: 'id',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '30%',
        },
        {
          text: t('component.headers.type'),
          value: 'type',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.hardwareSpecs'),
          value: 'hardwareSpecs',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.primaryFunction'),
          value: 'primaryFunction',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.componentEnv'),
          value: 'componentEnv',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.connectivity'),
          value: 'connectivity',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.protocols'),
          value: 'protocols',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.deploymentModel'),
          value: 'deploymentModel',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('component.headers.actions'),
          value: 'actions',
          align: 'center',
          sortable: false,
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    getMetaDataHeaders() {
      return [
        {
          text: 'Key',
          value: 'name',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '20%',
        },
        {
          text: 'Value',
          value: 'size',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
  },
  data() {
    return {
      downloadStarted: false,
      loading: false,
      components: [
        {
          name: 'Component A',
          id: 'c1a2b3d4-1234-5678-9abc-def012345678',
          type: 'Software Application',
          hardwareSpecs: 'Quad-core CPU, 8GB RAM',
          primaryFunction: 'Data Processing',
          componentEnv: 'Windows',
          connectivity: 'Wireless',
          protocols: ['MQTT', 'HTTP'],
          deploymentModel: 'Cloud-Based',
          userMetaData: [
            { key: 'Status', value: 'Draft' },
            { key: 'Created By', value: 'User A' },
          ],
        },
        {
          name: 'Component B',
          id: 'e2f3g4h5-2345-6789-0abc-def123456789',
          type: 'Host Device',
          hardwareSpecs: 'Dual-core CPU, 4GB RAM',
          primaryFunction: 'Network Routing',
          componentEnv: 'Linux',
          connectivity: 'Ethernet',
          protocols: ['OPC', 'HTTP'],
          deploymentModel: 'On-Premise',
          userMetaData: [
            { key: 'Status', value: 'Draft' },
            { key: 'Created By', value: 'User A' },
          ],
        },
      ],
      search: null,
      deleteClicked: false,
      deleteItem: null,
      finalizeClicked: false,
      finalizeItem: null,
      studyInfo: null,
      previewAssetClicked: false,
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchComponents();
    this.$emit('loading', false);
    this.loading = false;
  },
  methods: {
    async fetchComponents() {},
    enableDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteClicked = true;
    },
    enableFinalizeDialog(item) {
      this.finalizeItem = item;
      this.finalizeClicked = true;
    },
    async deleteComponent() {
      this.loading = true;
      this.loading = false;
      this.deleteClicked = false;
    },
    async downloadDataset() {
      this.downloadStarted = true;
      this.downloadStarted = false;
    },
    async finalizeEvidence() {
      console.log('Sending request to /finalize the asset: ', this.finalizeItem.name);
      this.loading = true;
      this.loading = false;
      //await this.fetchAssets();
      this.finalizeClicked = false;
    },
    byteToUint8Array(byteArray) {
      var uint8Array = new Uint8Array(byteArray.length);
      for (var i = 0; i < uint8Array.length; i++) {
        uint8Array[i] = byteArray[i];
      }

      return uint8Array;
    },
    async enablePreviewAssetDialog(id) {
      try {
        const cleanId = id.startsWith('dicom/') ? id.substring(6) : id;
        const { data: rawStudyInformation } = await Fhir.getImagingStudy(cleanId);
        this.studyInfo = rawStudyInformation;
        this.previewAssetClicked = true;
      } catch (error) {
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      }
    },
    statusChipColor(status) {
      switch (status) {
        case 'Draft':
          return 'yellow';
        case 'Finalized':
          return 'green';
        default:
          return 'primary';
      }
    },
    hasUserMetadata(item) {
      return item.userMetaData.length > 1;
    },
  },
};
</script>
